import { useState } from 'react';
import { ChevronDown, ChevronRight, FolderOpen, Pencil, Trash2, GripVertical } from 'lucide-react';
import { FOLDER_ICONS, FOLDER_COLORS, getFolderColor, MAX_FOLDER_NAME_LENGTH } from '../../config/folders';
import { PageRow } from './PageRow';
import type { Page, FolderSettings } from '../../types';

interface FolderSectionProps {
  folderName: string;
  pages: Page[];
  settings: FolderSettings;
  isCollapsed: boolean;
  isTeamTab: boolean;
  isMineTab: boolean;
  renamingFolder: string | null;
  renameValue: string;
  folderError: string | null;
  editingFolderSettings: string | null;
  duplicateIsPending: boolean;
  onToggleCollapse: (name: string) => void;
  onStartRename: (name: string, value: string) => void;
  onRenameChange: (value: string) => void;
  onRenameSubmit: (oldName: string, newName: string) => void;
  onCancelRename: () => void;
  onDeleteFolder: (name: string) => void;
  onToggleSettings: (name: string | null) => void;
  onUpdateIcon: (folderName: string, icon: string) => void;
  onUpdateColor: (folderName: string, color: string) => void;
  onMoveToFolder: (id: string) => void;
  onToggleStatus: (page: Page) => void;
  onDuplicate: (id: string) => void;
  onDelete: (id: string) => void;
  onReorderPages: (folderName: string, orderedIds: string[]) => void;
}

export function FolderSection({
  folderName,
  pages,
  settings,
  isCollapsed,
  isTeamTab,
  isMineTab,
  renamingFolder,
  renameValue,
  folderError,
  editingFolderSettings,
  duplicateIsPending,
  onToggleCollapse,
  onStartRename,
  onRenameChange,
  onRenameSubmit,
  onCancelRename,
  onDeleteFolder,
  onToggleSettings,
  onUpdateIcon,
  onUpdateColor,
  onMoveToFolder,
  onToggleStatus,
  onDuplicate,
  onDelete,
  onReorderPages,
}: FolderSectionProps) {
  const colorInfo = getFolderColor(settings.color);
  const isRenaming = renamingFolder === folderName;
  const isEditingSettings = editingFolderSettings === folderName;
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);

  return (
    <div className={`bg-white rounded-xl border ${colorInfo.border} shadow-sm overflow-hidden`}>
      {/* Folder header */}
      <div className={`flex items-center justify-between px-4 py-3 ${colorInfo.bg} border-b ${colorInfo.border}`}>
        <button
          onClick={() => onToggleCollapse(folderName)}
          className={`flex items-center gap-2 font-semibold text-sm ${colorInfo.text} hover:opacity-80 transition-opacity`}
        >
          {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          <span>{settings.icon}</span>
          {isRenaming ? (
            <form
              className="inline-flex items-center gap-1"
              onSubmit={(e) => {
                e.preventDefault();
                const name = renameValue.trim();
                if (name && name !== folderName) {
                  onRenameSubmit(folderName, name);
                } else {
                  onCancelRename();
                }
              }}
            >
              <input
                type="text"
                autoFocus
                maxLength={MAX_FOLDER_NAME_LENGTH}
                value={renameValue}
                onChange={(e) => onRenameChange(e.target.value)}
                className={`border rounded px-2 py-0.5 text-sm bg-white ${folderError && isRenaming ? 'border-red-400' : 'border-gray-300'}`}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => { if (e.key === 'Escape') onCancelRename(); }}
              />
              {folderError && isRenaming && <span className="text-xs text-red-500 ml-1">{folderError}</span>}
            </form>
          ) : (
            <span>{folderName}</span>
          )}
          <span className="text-xs font-normal opacity-70">({pages.length})</span>
        </button>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onToggleSettings(isEditingSettings ? null : folderName)}
            title="Ordner anpassen"
            aria-label="Ordner anpassen"
            className="p-1.5 text-gray-400 hover:text-indigo-600 rounded transition-colors"
          >
            <FolderOpen className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => onStartRename(folderName, folderName)}
            title="Ordner umbenennen"
            aria-label="Ordner umbenennen"
            className="p-1.5 text-gray-400 hover:text-indigo-600 rounded transition-colors"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => onDeleteFolder(folderName)}
            title="Ordner löschen (Seiten bleiben erhalten)"
            aria-label="Ordner löschen"
            className="p-1.5 text-gray-400 hover:text-red-600 rounded transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      {/* Folder settings panel */}
      {isEditingSettings && (
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50 space-y-2">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Icon</label>
            <div className="flex gap-1.5 flex-wrap">
              {FOLDER_ICONS.map((icon) => (
                <button
                  key={icon}
                  onClick={() => onUpdateIcon(folderName, icon)}
                  className={`w-8 h-8 rounded-lg text-base flex items-center justify-center ${settings.icon === icon ? 'bg-indigo-100 ring-2 ring-indigo-500' : 'bg-white border border-gray-200 hover:bg-gray-50'}`}
                >
                  {icon}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Farbe</label>
            <div className="flex gap-1.5 flex-wrap">
              {FOLDER_COLORS.map((c) => (
                <button
                  key={c.value}
                  onClick={() => onUpdateColor(folderName, c.value)}
                  className={`px-3 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text} ${settings.color === c.value ? 'ring-2 ring-indigo-500' : 'hover:opacity-80'}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* Folder pages */}
      {!isCollapsed && (
        <div className="divide-y divide-gray-100">
          {pages.length === 0 ? (
            <p className="px-4 py-3 text-sm text-gray-400 italic">Leerer Ordner</p>
          ) : (
            pages.map((p, idx) => (
              <div
                key={p.id}
                className={`flex items-center relative ${draggingId === p.id ? 'opacity-40' : ''} ${dragOverIdx === idx ? 'ring-2 ring-inset ring-indigo-400' : ''}`}
                draggable={isMineTab && pages.length > 1}
                onDragStart={(e) => {
                  setDraggingId(p.id);
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', p.id);
                }}
                onDragEnd={() => { setDraggingId(null); setDragOverIdx(null); }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  if (draggingId && draggingId !== p.id) setDragOverIdx(idx);
                }}
                onDragLeave={() => setDragOverIdx(null)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOverIdx(null);
                  if (!draggingId || draggingId === p.id) return;
                  const newOrder = pages.map((pg) => pg.id).filter((id) => id !== draggingId);
                  newOrder.splice(idx, 0, draggingId);
                  onReorderPages(folderName, newOrder);
                  setDraggingId(null);
                }}
              >
                {isMineTab && pages.length > 1 && (
                  <div className="pl-2 cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 transition-colors">
                    <GripVertical className="w-4 h-4" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <PageRow
                    page={p}
                    isMineTab={isMineTab}
                    isTeamTab={isTeamTab}
                    onMoveToFolder={onMoveToFolder}
                    onToggleStatus={onToggleStatus}
                    onDuplicate={onDuplicate}
                    onDelete={onDelete}
                    duplicateIsPending={duplicateIsPending}
                  />
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
